import { useEffect, useState } from 'react';
import GupshupWabaTab from '@/components/GupshupWabaTab';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Link2, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const NumerosWabaTab = () => {
  const [twilioConfig, setTwilioConfig] = useState({ configured: false, phone: '', sid_masked: '', account_sid: '' });
  const [twilioTemplateCount, setTwilioTemplateCount] = useState(0);
  const [loadingTwilio, setLoadingTwilio] = useState(true);
  const [editingTwilio, setEditingTwilio] = useState(false);
  const [savingTwilio, setSavingTwilio] = useState(false);
  const [deletingTwilio, setDeletingTwilio] = useState(false);
  const [twilioForm, setTwilioForm] = useState({ account_sid: '', auth_token: '', phone: '' });

  const loadTwilio = async () => {
    setLoadingTwilio(true);
    try {
      const [cfg, templates] = await Promise.all([
        api.get('/whatsapp/twilio-config').catch(() => ({ data: { configured: false, phone: '', sid_masked: '', account_sid: '' } })),
        api.get('/whatsapp/templates/all', { params: { provider: 'twilio', page: 1, page_size: 1 } }).catch(() => ({ data: { total: 0, items: [] } })),
      ]);

      const nextConfig = {
        configured: Boolean(cfg.data?.configured),
        phone: cfg.data?.phone || '',
        sid_masked: cfg.data?.sid_masked || '',
        account_sid: cfg.data?.account_sid || '',
      };

      setTwilioConfig({
        ...nextConfig,
      });
      const total = Number(templates.data?.total);
      const fallback = Array.isArray(templates.data?.items) ? templates.data.items.length : 0;
      setTwilioTemplateCount(Number.isFinite(total) ? total : fallback);

      if (!editingTwilio) {
        setTwilioForm({
          account_sid: nextConfig.account_sid || '',
          auth_token: '',
          phone: nextConfig.phone || '',
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar sección Twilio');
    } finally {
      setLoadingTwilio(false);
    }
  };

  useEffect(() => { loadTwilio(); }, []);

  const startEditTwilio = () => {
    setTwilioForm({
      account_sid: twilioConfig.account_sid || '',
      auth_token: '',
      phone: twilioConfig.phone || '',
    });
    setEditingTwilio(true);
  };

  const cancelEditTwilio = () => {
    setEditingTwilio(false);
    setTwilioForm({
      account_sid: twilioConfig.account_sid || '',
      auth_token: '',
      phone: twilioConfig.phone || '',
    });
  };

  const saveTwilioChanges = async () => {
    if (!twilioForm.account_sid || !twilioForm.phone) {
      toast.error('Account SID y número Twilio son requeridos');
      return;
    }

    setSavingTwilio(true);
    try {
      await api.post('/whatsapp/twilio-config', {
        account_sid: twilioForm.account_sid,
        auth_token: twilioForm.auth_token,
        phone: twilioForm.phone,
      });
      toast.success('Configuración Twilio actualizada');
      setEditingTwilio(false);
      await loadTwilio();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar Twilio');
    } finally {
      setSavingTwilio(false);
    }
  };

  const deleteTwilioConfig = async () => {
    const confirmed = window.confirm('¿Desconectar Twilio? Se eliminarán las plantillas guardadas.');
    if (!confirmed) return;

    setDeletingTwilio(true);
    try {
      await api.delete('/whatsapp/twilio-config');
      toast.success('Twilio desconectado y plantillas eliminadas');
      setEditingTwilio(false);
      await loadTwilio();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar la configuración Twilio');
    } finally {
      setDeletingTwilio(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-numeros-root">
      <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-numeros-gupshup-section">
        <CardHeader>
          <CardTitle className="text-white text-sm">Gupshup WABA</CardTitle>
        </CardHeader>
        <CardContent>
          <GupshupWabaTab />
        </CardContent>
      </Card>

      <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-numeros-twilio-section">
        <CardHeader>
          <CardTitle className="text-white text-sm">Twilio</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTwilio ? (
            <p className="text-xs text-zinc-400">Cargando configuración Twilio...</p>
          ) : (
            <div className="rounded-xl border border-white/10 bg-black/20 p-3 flex flex-wrap items-center justify-between gap-3" data-testid="whatsapp-numeros-twilio-card">
              <div>
                <p className="text-sm text-white font-medium" data-testid="whatsapp-numeros-twilio-phone">{twilioConfig.phone || 'Sin número configurado'}</p>
                <p className="text-[11px] text-zinc-400" data-testid="whatsapp-numeros-twilio-sid">{twilioConfig.sid_masked || 'Sin SID'}</p>
              </div>

              <div className="flex items-center gap-2 flex-wrap" data-testid="whatsapp-numeros-twilio-actions">
                <Badge className={`${twilioConfig.configured ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'} border`} data-testid="whatsapp-numeros-twilio-status">
                  {twilioConfig.configured ? 'CONECTADO' : 'NO CONFIGURADO'}
                </Badge>
                <Button variant="outline" size="sm" onClick={loadTwilio} data-testid="whatsapp-numeros-twilio-view-templates-btn">
                  <Link2 className="w-3.5 h-3.5 mr-1" /> Ver plantillas ({twilioTemplateCount})
                </Button>
                <Button variant="outline" size="sm" onClick={startEditTwilio} data-testid="whatsapp-numeros-twilio-edit-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> ✏ Editar
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30"
                  onClick={deleteTwilioConfig}
                  disabled={deletingTwilio}
                  data-testid="whatsapp-numeros-twilio-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> {deletingTwilio ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </div>
            </div>
          )}

          {editingTwilio ? (
            <div className="mt-3 rounded-xl border border-blue-500/20 bg-blue-500/5 p-3 space-y-3" data-testid="whatsapp-numeros-twilio-edit-form">
              <div>
                <Label className="text-xs text-zinc-300">Account SID</Label>
                <Input
                  value={twilioForm.account_sid}
                  onChange={(e) => setTwilioForm((prev) => ({ ...prev, account_sid: e.target.value }))}
                  placeholder="ACxxxxxxxxxxxxxxxx"
                  data-testid="whatsapp-numeros-twilio-edit-account-sid-input"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-300">Auth Token</Label>
                <Input
                  type="password"
                  value={twilioForm.auth_token}
                  onChange={(e) => setTwilioForm((prev) => ({ ...prev, auth_token: e.target.value }))}
                  placeholder="Dejar vacío para conservar el token actual"
                  data-testid="whatsapp-numeros-twilio-edit-auth-token-input"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-300">Número Twilio WhatsApp</Label>
                <Input
                  value={twilioForm.phone}
                  onChange={(e) => setTwilioForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="+5215512345678"
                  data-testid="whatsapp-numeros-twilio-edit-phone-input"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={cancelEditTwilio} data-testid="whatsapp-numeros-twilio-edit-cancel-btn">
                  Cancelar
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-500"
                  onClick={saveTwilioChanges}
                  disabled={savingTwilio}
                  data-testid="whatsapp-numeros-twilio-edit-save-btn"
                >
                  {savingTwilio ? 'Guardando...' : 'Guardar cambios'}
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
